import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createRowActions } from "@msuite/ui/table";
import { Tag } from "@msuite/ui/tag";
import { guardString } from "@msuite/utils/strings";
import type { ReturnReconciliationListItem } from "@msuite/utils/types";
import { RowActions } from "./row-actions";
import { getArticleNumberString } from "@msuite/utils/stock";

const columnHelper = createColumnHelper<ReturnReconciliationListItem>();

/**
 * One row per article the project was ever sent or has returned.
 *
 * The three quantity columns are the account read left to right: what went out, what is standing at
 * the goods-in door, what is on a shelf. `outstandingQuantity` subtracts both inbound figures, so
 * it does not move when somebody walks a trolley to a rack — that shifts a quantity from the middle
 * column to the right one and settles nothing.
 *
 * `outstandingQuantity` sorts on its number but renders as one of three things, because two of the
 * values are answers rather than quantities: zero means the article is settled, and below zero
 * means more came back than this project's orders recorded — which is what happens when sites swap
 * material between jobs, not a fault.
 *
 * The number column displays the composed form and **sorts on `articleNumber` alone**, which is only
 * unique within its product type — so sorting by it interleaves groups. The server's own order is
 * the whole triple, which is what the table opens on.
 */
export const returnReconciliationTableColumns = [
	columnHelper.accessor("articleNumber", {
		id: "articleNumber",
		header: localized("general.number"),
		cell: (cell) =>
			getArticleNumberString({
				articleGroupNumber: cell.row.original.articleGroupNumber,
				articleProductTypeNumber: cell.row.original.articleProductTypeNumber,
				number: cell.getValue(),
			}),
	}),
	columnHelper.accessor("articleName", {
		id: "articleName",
		header: localized("general.name"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	// Where it was picked from for this project, which is the shelf it should go back to.
	columnHelper.accessor("lastPickedLocationName", {
		id: "lastPickedLocationName",
		header: localized("stock.return_last_picked_from"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor("shippedQuantity", {
		id: "shippedQuantity",
		header: localized("stock.return_shipped"),
		cell: (cell) => String(cell.getValue()),
	}),
	// Counted at the gate and not yet on a shelf. Its own column rather than folded into the one
	// beside it: a site asking where its material is has two different answers — arrived, and put
	// away — and a single figure would have to pick one of them to be wrong about.
	columnHelper.accessor("receivedQuantity", {
		id: "receivedQuantity",
		header: localized("stock.return_received"),
		cell: (cell) => String(cell.getValue()),
	}),
	columnHelper.accessor("returnedQuantity", {
		id: "returnedQuantity",
		header: localized("stock.return_returned"),
		cell: (cell) => String(cell.getValue()),
	}),
	columnHelper.accessor("outstandingQuantity", {
		id: "outstandingQuantity",
		header: localized("stock.return_outstanding"),
		cell: (cell) => {
			const outstanding = cell.getValue();
			if (outstanding === 0) {
				return (
					<Tag
						variant="plain"
						color="emerald"
					>
						{localized("stock.return_net_zero")}
					</Tag>
				);
			}
			if (outstanding < 0) {
				return (
					<Tag
						variant="plain"
						color="amber"
					>
						{localized("stock.return_over_returned")}
					</Tag>
				);
			}
			return String(outstanding);
		},
	}),
	createRowActions(RowActions),
];
